import { useSelector } from 'react-redux';
import { routes } from 'settings/navigation/routes';
import { Stages, Button } from 'components';
import { AssignConnectionModal, Modal } from 'components-antd';
import { useDispatch } from 'react-redux';
import styles from './styles.module.scss';
import InviteClientRetentionMode from './StageParts/InviteClientRetentionMode';
import { inviteClientSteps, YesNoOptions } from 'types/inviteClient';
import { InviteClientSearchSummary } from './StageParts/InviteClientSearchSummary';
import { InviteClientNotifications } from './StageParts/InviteClientNotifications';
import { useCallback, useEffect, useState } from 'react';
import SendInvite from '../SendInvite';
import classNames from 'classnames';
import { handleSendClientInviteForm } from 'store/effects/clientsList';
import { ClientCategory } from 'types';
import { useHistory } from 'react-router-dom';
import { postReinviteClientById } from 'api/clientsList';
import {
  getActiveClientTransactionInstance,
  getInviteClientDrawerValues,
  isInviteClientModalOpened,
} from 'store/selectors/app';
import {
  appResetClientDriverAction,
  appSetInviteClientAddressesAction,
  appSetShowInviteClientAction,
} from 'store/actions/app';
import {
  ClientDetails,
  InviteClientSearchInstance,
  InviteClientWishList,
  InviteClientTransactionInstance,
  InviteClientPreApproval,
} from './StageParts';

const InviteClient = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isInviteOpened = useSelector(isInviteClientModalOpened);
  const [inviteLink, setInviteLink] = useState<null | string>(null);
  const [connectionStep, setConnectionStep] = useState(false);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(5);
  const [step, setStep] = useState(1);
  const [userId, setUserId] = useState();
  const [skipped, setSkipped] = useState(false);
  const clientValues = useSelector(getInviteClientDrawerValues);
  const activeClientTransactionInstance = useSelector(getActiveClientTransactionInstance);
  const role = clientValues?.inviteClientValues?.clientType || [];
  const bothRoles = [ClientCategory.Buyer, ClientCategory.Seller].every((value) =>
    role.includes(value),
  );
  const sellerRole = role.includes(ClientCategory.Seller);

  const redirect = () => {
    history.push(routes.transactionCreate + `?invitedClient=${userId}`);
    dispatch(appResetClientDriverAction({}));
  };

  useEffect(() => {
    if (step === 2 && !bothRoles && sellerRole) {
      if (count > 0) {
        const timer = setInterval(() => {
          setCount((prevCount) => prevCount - 1);
        }, 1000);
        return () => clearInterval(timer);
      } else {
        redirect();
      }
    }
  }, [count, step]);

  const InviteClientConnections = ({ ...rest }) => (
    <AssignConnectionModal
      disableOnEmpty={false}
      closeOnClientEmpty
      noModal
      onInvite={onSubmit}
      mode="ClientInvite"
      {...rest}
    />
  );

  const InviteClientRetentionModeExtend = useCallback(
    ({ ...rest }) => <InviteClientRetentionMode {...rest} onSubmit={onSubmit} />,
    [],
  );

  const InviteClientTransactionInstanceExtend = useCallback(
    ({ ...rest }) => <InviteClientTransactionInstance {...rest} onSubmit={onSubmit} />,
    [],
  );

  const InviteClientNotificationsExtend = useCallback(
    ({ ...rest }) => <InviteClientNotifications {...rest} onSubmit={onSubmit} />,
    [],
  );

  const onSubmit = ({
    connections,
    onNext,
    activeClientTransactionValues,
    clientNotificationValues,
    formValues,
  }) => {
    setLoading(true);
    const values = {
      ...(formValues || {}),
      ...(clientNotificationValues || clientValues?.clientNotificationValues || {}),
      ...(activeClientTransactionValues || clientValues?.activeClientTransactionInstance || {}),
    };
    let notificationPreferences;
    if (values.isEnabled === YesNoOptions.Yes) {
      notificationPreferences = JSON.parse(JSON.stringify(values.NotificationPreferences));
      const propertyNotificationItems = notificationPreferences[0].NotificationItems;
      const newListingNotification = propertyNotificationItems.find((notification) =>
        notification.Text.includes('There is a new listing that matches'),
      );
      const suggestingNotification = propertyNotificationItems.find((notification) =>
        notification.Text.includes('I suggest a property for'),
      );
      newListingNotification.Text = 'There is a new listing that matches my search criteria.';
      newListingNotification.SecondSubText = 'Let me know:';
      suggestingNotification.Text = 'My agent highlights a property.';
      delete suggestingNotification.SubText;
    }
    const body = {
      ...(connections && connections.length ? { AssignedAgentIds: connections } : {}),
      ...(bothRoles || sellerRole
        ? activeClientTransactionValues || activeClientTransactionInstance
        : {}),
      ...(values.isEnabled ? { NotificationPreferences: notificationPreferences } : {}),
      ...(values.FromRetentionMode ? { FromRetentionMode: values.FromRetentionMode } : {}),
      ...(clientValues?.inviteClientValues?.ClientTags?.length
        ? { ClientTags: clientValues.inviteClientValues.ClientTags }
        : {}),
    };
    dispatch(
      handleSendClientInviteForm({ values: body }, (err, res) => {
        if (err) {
          onNext(inviteClientSteps.ClientDetails);
        } else {
          dispatch(appSetInviteClientAddressesAction([]));
          setUserId(res.data.result.Id);
          setInviteLink(res.data.result.InviteLink);
          setConnectionStep(true);
        }
        setLoading(false);
      }),
    );
  };

  const handleSubmit = async () => {
    setLoading(true);
    await postReinviteClientById({ id: userId });
    setLoading(false);
    handleNext();
  };

  const handleNext = () => {
    setStep(2);
  };

  const icon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none">
      <rect width="64" height="64" fill="white" />
      <g>
        <path
          d="M6.56905 29.6418C5.31614 28.9985 5.37489 27.1354 6.66023 26.5596C15.3117 22.6842 20.0732 18.1463 24.8586 8.1904C25.4672 6.92426 27.3242 6.92763 27.9308 8.19476C32.0409 16.7815 36.6311 21.8691 46.3878 26.5512C47.6264 27.1456 47.6704 28.9451 46.455 29.5859C37.3915 34.3644 32.6416 38.9922 27.9204 48.1138C27.2952 49.3218 25.521 49.3143 24.9051 48.1015C20.1679 38.7734 15.4215 34.1869 6.56905 29.6418Z"
          fill="#FB913A"
        />
      </g>
      <g>
        <path
          d="M25.7636 37.653C24.5159 36.9997 24.5727 35.133 25.8521 34.5443C32.882 31.3098 36.8829 27.4679 40.8465 19.3838C41.4649 18.1225 43.3247 18.1257 43.9424 19.3874C47.368 26.3851 51.2545 30.6451 59.1945 34.539C60.428 35.1439 60.4686 36.9461 59.2577 37.5953C51.8613 41.5609 47.8532 45.4748 43.9299 52.9179C43.2956 54.1212 41.5196 54.1149 40.8947 52.9067C36.9644 45.3075 32.9691 41.4258 25.7636 37.653Z"
          fill="#FF576D"
        />
      </g>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M39.2483 22.4102C41.2865 23.8684 43.6317 25.2243 46.3897 26.5479C47.6284 27.1423 47.6723 28.9418 46.457 29.5826C39.8841 33.0479 35.5798 36.4341 31.9099 41.5124C30.1313 40.1306 28.1198 38.8863 25.7655 37.6536C24.5178 37.0003 24.5746 35.1336 25.8541 34.5449C31.9434 31.7431 35.76 28.4857 39.2483 22.4102Z"
        fill="#E4364D"
      />
    </svg>
  );

  const InviteClientInvite = ({ ...rest }) => (
    <SendInvite
      isLoading={loading}
      stepOne={
        <p>
          {`All set! Are you ready to send an email invitation to ${clientValues?.inviteClientValues?.firstName}? 🚀`}
        </p>
      }
      stepTwo={
        !bothRoles && sellerRole ? (
          <>
            <div className={styles.iconContainer}>{icon()}</div>
            <p>
              Awesome! <br />
              <span>
                {`Let's finish creating ${clientValues?.inviteClientValues?.firstName}'s listing...`}
                <br />
                {`You’ll be redirected in ${count} seconds.`}
              </span>
            </p>
            <Button title={'Continue'} className={styles.button} onClick={redirect} />
          </>
        ) : skipped ? (
          <p style={{ marginBottom: 0 }}>
            <span>
              An <b>email invitation</b> was not sent to{' '}
              <b>{`${clientValues?.inviteClientValues?.firstName} ${clientValues.inviteClientValues?.lastName}`}</b>
              . You can send one from their profile or you can invite them through the link below.
            </span>
          </p>
        ) : (
          <p>
            🎉
            <br /> An email invitation was sent to{' '}
            {`${clientValues?.inviteClientValues?.firstName} ${clientValues?.inviteClientValues?.lastName}`}
            .
          </p>
        )
      }
      onSubmit={handleSubmit}
      onSkip={() => {
        setSkipped(true);
        handleNext();
      }}
      inviteLink={inviteLink}
      step={step}
      hideCopy={step === 2 && !bothRoles && sellerRole}
      {...rest}
    />
  );

  return (
    <div className={styles.inviteClientContainer}>
      <Modal
        open={isInviteOpened}
        width={connectionStep ? 476 : 700}
        footer={null}
        onCancel={() => {
          dispatch(appSetShowInviteClientAction(false));
          dispatch(appResetClientDriverAction({}));
        }}
        className={classNames(styles.inviteModal, { [styles.connectionStep]: connectionStep })}
        closeIconStyles={styles.crossButton}
        maskClosable={step === 2}
        destroyOnClose={true}
      >
        <div className={styles.wrapper}>
          <Stages
            stages={[
              { id: inviteClientSteps.ClientDetails, Component: ClientDetails },
              {
                id: inviteClientSteps.ClientTransactionInstance,
                Component: InviteClientTransactionInstanceExtend,
              },
              { id: inviteClientSteps.ClientSearchInstance, Component: InviteClientSearchInstance },
              { id: inviteClientSteps.ClientWishList, Component: InviteClientWishList },
              {
                id: inviteClientSteps.ClientSearchInstanceSummary,
                Component: InviteClientSearchSummary,
              },
              {
                id: inviteClientSteps.ClientRetentionMode,
                Component: InviteClientRetentionModeExtend,
              },
              { id: inviteClientSteps.ClientPreApproval, Component: InviteClientPreApproval },
              {
                id: inviteClientSteps.ClientNotifications,
                Component: InviteClientNotificationsExtend,
              },
              { id: inviteClientSteps.ClientConnections, Component: InviteClientConnections },
              { id: inviteClientSteps.ClientInvite, Component: InviteClientInvite },
            ]}
            classNameProgressBackground={styles.progressBackground}
            classNameProgress={styles.progressBar}
            classNameWrapper={styles.stagesWrapper}
            showProgressBar={false}
          />
        </div>
      </Modal>
    </div>
  );
};

export default InviteClient;
